import { useState } from 'react';
import { Input } from '../../../shared/components/Input';
import { TextArea } from '../../../shared/components/TextArea';
import Button from '../../../shared/components/Button';
import {
  BRANCH_NAME_MIN,
  BRANCH_NAME_MAX,
  BRANCH_TEXT_MAX,
  type Branch,
  type CreateBranchPayload,
} from '../api/branchesApi';

/** Body fields shared by create/update — merchantId is injected by the page. */
export type BranchFormValues = Omit<CreateBranchPayload, 'merchantId'>;

interface Props {
  initial?: Branch | null;
  onSubmit: (values: BranchFormValues) => void;
  onCancel: () => void;
  isSaving: boolean;
}

/**
 * Create/edit a branch (PE-2139). Validation mirrors watchman's Create/UpdateBranchDto
 * so the user sees the failure before the round trip rather than a 400 from the server.
 */
export function BranchForm({ initial, onSubmit, onCancel, isSaving }: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    if (trimmedName.length < BRANCH_NAME_MIN) {
      setError(`Branch name must be at least ${BRANCH_NAME_MIN} characters`);
      return;
    }
    if (trimmedName.length > BRANCH_NAME_MAX) {
      setError(`Branch name cannot exceed ${BRANCH_NAME_MAX} characters`);
      return;
    }
    if (description.trim().length > BRANCH_TEXT_MAX) {
      setError(`Description cannot exceed ${BRANCH_TEXT_MAX} characters`);
      return;
    }
    if (address.trim().length > BRANCH_TEXT_MAX) {
      setError(`Address cannot exceed ${BRANCH_TEXT_MAX} characters`);
      return;
    }

    onSubmit({
      name: trimmedName,
      description: description.trim() || undefined,
      address: address.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Branch name"
        placeholder="e.g. Lekki Showroom"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={BRANCH_NAME_MAX}
      />

      <TextArea
        label="Address"
        placeholder="e.g. 12 Admiralty Way, Lekki Phase 1, Lagos"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        maxLength={BRANCH_TEXT_MAX}
      />

      <TextArea
        label="Description"
        placeholder="Anything staff should know about this location"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={BRANCH_TEXT_MAX}
      />

      {error && <p className="text-[13px] text-red-500">{error}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" size="sm" className="h-[40px]" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" className="h-[40px]" disabled={isSaving}>
          {isSaving ? 'Saving…' : initial ? 'Save changes' : 'Create branch'}
        </Button>
      </div>
    </form>
  );
}
